import type { Result } from "@pdfking/errors"
import { ArrayQ } from "@pdfking/structs"
import { RecordMerge } from "@pdfking/tfunc"

import type { AnyNode } from "#public"

import { ConfirmAllValid } from "../../internal/ConfirmAllValid"

type Container = AnyNode & { results: AnyNode[] | Record<string, AnyNode> }

/**
 * Set `error` on the node reached by following `path` (container child keys from
 * the given node down to a leaf), rebuilding each container on the way up and
 * recomputing its aggregate via `ConfirmAllValid` so the failure propagates to
 * the root. Number keys index array `results`; string keys index object
 * `results`. Used by cross-field validators that need to fail a deeply-nested
 * field (e.g. a job's `machine_size`) rather than a top-level one.
 */
export const FailAtPath = (
  node: AnyNode,
  path: (string | number)[],
  error: Result<unknown>
): AnyNode => {
  if (path.length === 0) return RecordMerge(node, error) as AnyNode

  const [key, ...rest] = path
  const { results } = node as Container
  const child = (results as Record<string, AnyNode>)[key as string]
  const next = FailAtPath(child, rest, error)
  const new_results = ArrayQ(results)
    ? results.map((r, i) => (i === key ? next : r))
    : { ...results, [key]: next }

  return RecordMerge(
    node,
    { results: new_results },
    ConfirmAllValid(new_results as Parameters<typeof ConfirmAllValid>[0])
  ) as AnyNode
}
